import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../services/api";
import { useDashboardStats, useRecentInvoices } from "../hooks/useDashboard";
import PalletCostTable from "../components/PalletCostTable";
import DropZone from "../components/DropZone";
import { invoices } from "../services/api";
import { computeSha256 } from "../utils/hash";
import { InvoiceStatus, InvoiceType } from "../types";
import type { Invoice } from "../types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(n: number): string {
  return `\u00A3${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function statusBadge(status: InvoiceStatus) {
  const map: Record<InvoiceStatus, { bg: string; text: string; label: string }> = {
    [InvoiceStatus.VERIFIED]: {
      bg: "bg-green-100",
      text: "text-green-800",
      label: "Verified",
    },
    [InvoiceStatus.ISSUES_FOUND]: {
      bg: "bg-red-100",
      text: "text-red-800",
      label: "Issues Found",
    },
    [InvoiceStatus.PROCESSING]: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      label: "Processing",
    },
    [InvoiceStatus.ERROR]: {
      bg: "bg-gray-100",
      text: "text-gray-800",
      label: "Error",
    },
  };
  const s = map[status];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

function typeBadge(type: InvoiceType) {
  const config: Record<InvoiceType, { bg: string; label: string }> = {
    [InvoiceType.HAULIER]: { bg: "bg-blue-100 text-blue-800", label: "Haulier" },
    [InvoiceType.WAREHOUSE]: { bg: "bg-purple-100 text-purple-800", label: "Warehouse" },
    [InvoiceType.SUNDRY]: { bg: "bg-orange-100 text-orange-800", label: "VAT Invoice" },
    [InvoiceType.HAULIER_BULK]: { bg: "bg-cyan-100 text-cyan-800", label: "Haulier - Bulk" },
    [InvoiceType.VAT_HAULAGE]: { bg: "bg-orange-100 text-orange-800", label: "VAT Inv Haulage" },
    [InvoiceType.VAT_STORAGE]: { bg: "bg-amber-100 text-amber-800", label: "VAT Inv Storage" },
    [InvoiceType.HAULIER_COMPANION]: { bg: "bg-yellow-100 text-yellow-800", label: "Box PDF (awaiting CSV)" },
  };
  const c = config[type] ?? { bg: "bg-gray-100 text-gray-800", label: type };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${c.bg}`}
    >
      {c.label}
    </span>
  );
}

// ─── Icons (inline SVGs) ──────────────────────────────────────────────────────

function DocumentStackIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: stats, isLoading: statsLoading, error: statsError } = useDashboardStats();
  const { data: invoiceData, isLoading: invoicesLoading, error: invoicesError } = useRecentInvoices();
  const [toast, setToast] = useState<{ message: string; type: "warning" | "error" } | null>(null);

  const handleUploadFiles = useCallback(
    async (files: File[]) => {
      const duplicates: string[] = [];

      // Process files one at a time
      for (const file of files) {
        try {
          const ct = file.type || "application/octet-stream";
          const hash = await computeSha256(file);
          const { upload_url, file_key } = await invoices.getUploadUrl(file.name, ct);
          await fetch(upload_url, {
            method: "PUT",
            headers: { "Content-Type": ct },
            body: file,
          });
          const { invoice } = await invoices.create({
            file_key,
            file_hash: hash,
            original_filename: file.name,
            content_type: ct,
          } as Parameters<typeof invoices.create>[0] & {
            file_hash: string;
            original_filename: string;
            content_type: string;
          });

          // If single file, navigate to it
          if (files.length === 1) {
            navigate(`/invoice/${invoice.id}`);
            return;
          }
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            duplicates.push(`${file.name}: ${err.message}`);
          }
          // Skip other errors silently
        }
      }

      // Show toast for duplicates
      if (duplicates.length > 0) {
        setToast({
          message: duplicates.length === 1
            ? `Duplicate skipped — ${duplicates[0]}`
            : `${duplicates.length} duplicates skipped:\n${duplicates.join("\n")}`,
          type: "warning",
        });
        setTimeout(() => setToast(null), 8000);
      }

      // Refresh dashboard data after upload
      queryClient.invalidateQueries({ queryKey: ["invoices", "recent"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard", "stats"] });
    },
    [navigate, queryClient],
  );

  // Derive total verified value from stats. The API may include this field.
  // Stats fields: total_invoices, total_issues, total_value_verified, total_overcharge_value, average_pallet_costs

  const recentInvoices: Invoice[] = invoiceData?.invoices?.slice(0, 5) ?? [];

  return (
    <div className="space-y-6">
      {/* ── Toast notification ── */}
      {toast && (
        <div
          className={`fixed right-4 top-4 z-50 max-w-md rounded-lg border px-4 py-3 shadow-lg ${
            toast.type === "warning"
              ? "border-amber-300 bg-amber-50 text-amber-800"
              : "border-red-300 bg-red-50 text-red-800"
          }`}
        >
          <div className="flex items-start gap-2">
            <svg className="mt-0.5 h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <p className="whitespace-pre-line text-sm font-medium">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="ml-auto shrink-0 text-current opacity-60 hover:opacity-100"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Invoice verification overview and statistics.
        </p>
      </div>

      {/* ── Error state ── */}
      {statsError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load dashboard statistics. Please try refreshing the page.
        </div>
      )}

      {/* ── Loading state ── */}
      {statsLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-100"
            />
          ))}
        </div>
      )}

      {/* ── Summary Strip ── */}
      {stats && (
        <div className="flex flex-wrap items-stretch gap-4">
          {/* Invoices */}
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <DocumentStackIcon />
            <div>
              <p className="text-xs text-gray-500">Invoices</p>
              <p className="text-lg font-bold text-gray-900">{(stats.total_invoices ?? 0).toLocaleString()}</p>
            </div>
          </div>
          {/* Issues */}
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <WarningIcon />
            <div>
              <p className="text-xs text-gray-500">Issues</p>
              <p className="text-lg font-bold text-red-600">{(stats.total_issues ?? 0).toLocaleString()}</p>
            </div>
          </div>
          {/* Haulier */}
          <div className="flex items-center gap-4 rounded-xl border border-indigo-200 bg-white px-4 py-3 shadow-sm">
            <div>
              <p className="text-xs font-semibold text-indigo-600">Haulier Verified</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(stats.haulier?.total_value_verified ?? 0)}</p>
            </div>
            <div className="border-l border-gray-200 pl-4">
              <p className="text-xs text-gray-500">Overcharge</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(stats.haulier?.total_overcharge_value ?? 0)}</p>
            </div>
          </div>
          {/* Warehouse */}
          <div className="flex items-center gap-4 rounded-xl border border-purple-200 bg-white px-4 py-3 shadow-sm">
            <div>
              <p className="text-xs font-semibold text-purple-600">Warehouse Verified</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(stats.warehouse?.total_value_verified ?? 0)}</p>
            </div>
            <div className="border-l border-gray-200 pl-4">
              <p className="text-xs text-gray-500">Overcharge</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(stats.warehouse?.total_overcharge_value ?? 0)}</p>
            </div>
          </div>
          {/* Processing count */}
          <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 shadow-sm ${
            (stats.processing_count ?? 0) > 0
              ? "border-blue-200 bg-blue-50"
              : "border-gray-200 bg-white"
          }`}>
            {(stats.processing_count ?? 0) > 0 ? <SpinnerIcon /> : <CheckIcon />}
            <div>
              <p className={`text-xs ${(stats.processing_count ?? 0) > 0 ? "text-blue-600" : "text-gray-500"}`}>
                {(stats.processing_count ?? 0) > 0 ? "Processing" : "Queue Clear"}
              </p>
              <p className={`text-lg font-bold ${(stats.processing_count ?? 0) > 0 ? "text-blue-700" : "text-green-600"}`}>
                {(stats.processing_count ?? 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Recent Uploads ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Uploads
          </h2>
        </div>

        {invoicesLoading && !recentInvoices.length && (
          <div className="flex items-center justify-center py-12">
            <svg
              className="h-6 w-6 animate-spin text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span className="ml-2 text-sm text-gray-500">Loading invoices...</span>
          </div>
        )}

        {invoicesError && !recentInvoices.length && (
          <div className="px-6 py-8 text-center text-sm text-red-600">
            Failed to load recent invoices.
          </div>
        )}

        {!invoicesLoading && recentInvoices.length === 0 && !invoicesError && (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            No invoices uploaded yet. Use the drop zone below to get started.
          </div>
        )}

        {recentInvoices.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="px-6 py-3">Invoice #</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Uploaded By</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Issues</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentInvoices.map((inv) => (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/invoice/${inv.id}`)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-6 py-3 font-medium text-blue-600">
                      {inv.invoice_number || "—"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3">
                      {typeBadge(inv.invoice_type)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {inv.invoice_date ? formatDate(inv.invoice_date) : "—"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {inv.uploaded_by}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3">
                      {statusBadge(inv.status)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {inv.summary
                        ? (
                            inv.summary.overcharged_count +
                            inv.summary.undercharged_count +
                            inv.summary.rate_not_found_count +
                            inv.summary.duplicate_count
                          ).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Average Pallet Cost ── */}
      {stats && <PalletCostTable stats={stats} />}

      {/* ── Quick Upload ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Quick Upload
        </h2>
        <DropZone onFiles={handleUploadFiles} />
      </div>
    </div>
  );
}
